/** Building blocks shared by the settings, staff, subscription and profile screens. */
import CheckRounded from '@mui/icons-material/CheckRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import LockPersonRounded from '@mui/icons-material/LockPersonRounded';
import VisibilityOffRounded from '@mui/icons-material/VisibilityOffRounded';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import {
  alpha,
  Box,
  Button,
  ButtonBase,
  Card,
  Collapse,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  type TextFieldProps,
} from '@mui/material';
import { forwardRef, useState, type FocusEvent, type ReactNode } from 'react';
import { EmptyState } from '../../components/states';
import { passwordStrength, STRENGTH_LABELS } from './lib';

/** A settings section: title + description on the left (wide screens), content on the right. */
export function SectionCard({
  title,
  description,
  icon,
  children,
  footer,
  action,
}: {
  title: string;
  description?: ReactNode;
  icon?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Card sx={{ overflow: 'visible' }}>
      <Stack
        direction="row"
        spacing={1.75}
        sx={{
          alignItems: 'flex-start',
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 2.75 },
          pb: 2,
        }}
      >
        {icon && <IconTile>{icon}</IconTile>}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5">{title}</Typography>
          {description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {description}
            </Typography>
          )}
        </Box>
        {action}
      </Stack>
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>{children}</Box>
      {footer}
    </Card>
  );
}

export function IconTile({
  children,
  color = 'primary',
  size = 40,
}: {
  children: ReactNode;
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info';
  size?: number;
}) {
  return (
    <Box
      sx={(t) => {
        const c = (t.vars ?? t).palette[color].main;
        return {
          width: size,
          height: size,
          flexShrink: 0,
          borderRadius: `${String(Math.round(size * 0.3))}px`,
          display: 'grid',
          placeItems: 'center',
          color: c,
          background: `linear-gradient(135deg, color-mix(in srgb, ${c} 16%, transparent), color-mix(in srgb, ${c} 6%, transparent))`,
          border: `1px solid color-mix(in srgb, ${c} 18%, transparent)`,
          '& svg': { fontSize: Math.round(size * 0.5) },
        };
      }}
    >
      {children}
    </Box>
  );
}

/** Floating, sticky save bar that appears once a form has unsaved changes. */
export function SaveBar({
  dirty,
  saving,
  disabled,
  onReset,
  label = 'Save changes',
}: {
  dirty: boolean;
  saving: boolean;
  disabled?: boolean;
  onReset: () => void;
  label?: string;
}) {
  return (
    <Box sx={{ position: 'sticky', bottom: 16, zIndex: 5, mt: 2.5 }}>
      <Collapse in={dirty} unmountOnExit>
        <Stack
          direction="row"
          spacing={1.25}
          sx={(t) => ({
            alignItems: 'center',
            mx: 'auto',
            maxWidth: 640,
            pl: 2,
            pr: 1,
            py: 1,
            borderRadius: '14px',
            border: 1,
            borderColor: 'divider',
            bgcolor: alpha(t.palette.background.paper, 0.86),
            backdropFilter: 'blur(14px) saturate(180%)',
            boxShadow: '0 18px 36px -12px rgba(16,24,40,0.28), 0 4px 10px -4px rgba(16,24,40,0.1)',
            ...t.applyStyles('dark', { bgcolor: alpha('#151D2B', 0.9) }),
          })}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              flexShrink: 0,
              borderRadius: '50%',
              bgcolor: 'warning.main',
              animation: 'sm-pulse 1.6s ease-in-out infinite',
              '@keyframes sm-pulse': { '50%': { opacity: 0.35 } },
            }}
          />
          <Typography variant="body2" noWrap sx={{ flex: 1, fontWeight: 600 }}>
            Unsaved changes
          </Typography>
          <Button size="small" onClick={onReset} disabled={saving} sx={{ color: 'text.secondary' }}>
            Discard
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="small"
            loading={saving}
            disabled={disabled}
          >
            {label}
          </Button>
        </Stack>
      </Collapse>
    </Box>
  );
}

/** A labelled on/off setting row. */
export function SettingSwitch({
  title,
  description,
  checked,
  onChange,
  disabled,
}: {
  title: string;
  description?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <Stack
      component="label"
      direction="row"
      spacing={2}
      sx={{
        alignItems: 'center',
        py: 1.75,
        cursor: disabled ? 'default' : 'pointer',
        '& + &': { borderTop: 1, borderColor: 'divider' },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        )}
      </Box>
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        slotProps={{ input: { 'aria-label': title } }}
      />
    </Stack>
  );
}

/** Shown in place of a screen when the user lacks the permission to view it. */
export function NoAccess({ what }: { what: string }) {
  return (
    <Card>
      <EmptyState
        icon={<LockPersonRounded />}
        title="You don’t have access"
        body={`Ask the shop owner to give your role permission to view ${what}.`}
      />
    </Card>
  );
}

/** Password input with a show/hide toggle and an optional strength meter. */
export const PasswordField = forwardRef<
  HTMLInputElement,
  TextFieldProps & { strength?: string | undefined }
>(function PasswordField({ strength, helperText, sx, ...props }, ref) {
  const [show, setShow] = useState(false);
  const score = strength === undefined ? null : passwordStrength(strength);
  const colors = ['', 'error.main', 'warning.main', 'info.main', 'success.main'];
  return (
    <Box sx={sx}>
      <TextField
        {...props}
        inputRef={ref}
        type={show ? 'text' : 'password'}
        helperText={helperText}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  edge="end"
                  aria-label={show ? 'Hide password' : 'Show password'}
                  onClick={() => setShow((s) => !s)}
                  sx={{ color: 'text.secondary' }}
                >
                  {show ? (
                    <VisibilityOffRounded fontSize="small" />
                  ) : (
                    <VisibilityRounded fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
      {score !== null && score > 0 && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 1 }}>
          <Stack direction="row" spacing={0.5} sx={{ flex: 1 }}>
            {[1, 2, 3, 4].map((i) => (
              <Box
                key={i}
                sx={{
                  flex: 1,
                  height: 4,
                  borderRadius: 999,
                  bgcolor: i <= score ? colors[score] : 'action.hover',
                  transition: 'background-color 160ms',
                }}
              />
            ))}
          </Stack>
          <Typography
            variant="caption"
            sx={{ fontWeight: 650, color: colors[score], minWidth: 44, textAlign: 'right' }}
          >
            {STRENGTH_LABELS[score]}
          </Typography>
        </Stack>
      )}
    </Box>
  );
});

/** Read-only value with a copy button (invite links). */
export function CopyField({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <TextField
      label={label}
      value={value}
      slotProps={{
        htmlInput: {
          readOnly: true,
          onFocus: (e: FocusEvent<HTMLInputElement>) => e.currentTarget.select(),
        },
        input: {
          sx: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 },
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title={copied ? 'Copied' : 'Copy link'}>
                <IconButton
                  edge="end"
                  aria-label="Copy link"
                  onClick={() => {
                    void navigator.clipboard.writeText(value).then(() => {
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1800);
                    });
                  }}
                  sx={{ color: copied ? 'success.main' : 'text.secondary' }}
                >
                  {copied ? (
                    <CheckRounded fontSize="small" />
                  ) : (
                    <ContentCopyRounded fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

/** Two-column field grid that stacks on phones. */
export function FieldGrid({ children, columns = 2 }: { children: ReactNode; columns?: 2 | 3 }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: 'minmax(0,1fr)',
          sm: `repeat(${String(columns)}, minmax(0, 1fr))`,
        },
      }}
    >
      {children}
    </Box>
  );
}

/** A radio group drawn as selectable cards. */
export function ChoiceCards<T extends string>({
  value,
  onChange,
  options,
  disabled,
  label,
  columns = 2,
}: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; title: string; description?: ReactNode; icon?: ReactNode }[];
  disabled?: boolean;
  label: string;
  columns?: 2 | 3;
}) {
  return (
    <Box
      role="radiogroup"
      aria-label={label}
      sx={{
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns: {
          xs: 'minmax(0,1fr)',
          sm: `repeat(${String(columns)}, minmax(0, 1fr))`,
        },
      }}
    >
      {options.map((o) => {
        const selected = o.value === value;
        return (
          <ButtonBase
            key={o.value}
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => onChange(o.value)}
            sx={(t) => {
              const p = (t.vars ?? t).palette.primary.main;
              return {
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                textAlign: 'left',
                gap: 1.5,
                p: 2,
                borderRadius: '14px',
                border: '1px solid',
                borderColor: selected ? p : (t.vars ?? t).palette.divider,
                bgcolor: selected
                  ? `color-mix(in srgb, ${p} 6%, ${(t.vars ?? t).palette.background.paper})`
                  : 'background.paper',
                boxShadow: selected
                  ? `0 0 0 3px color-mix(in srgb, ${p} 16%, transparent)`
                  : '0 1px 2px rgba(16,24,40,0.04)',
                transition: 'border-color 140ms, box-shadow 140ms, background-color 140ms',
                '&:hover': { borderColor: selected ? p : 'grey.300' },
                '&.Mui-disabled': { opacity: selected ? 1 : 0.6 },
              };
            }}
          >
            {o.icon && (
              <Box sx={{ color: selected ? 'primary.main' : 'text.secondary', mt: '2px' }}>
                {o.icon}
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 650 }}>{o.title}</Typography>
              {o.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {o.description}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                width: 18,
                height: 18,
                mt: '2px',
                flexShrink: 0,
                borderRadius: '50%',
                border: '1.5px solid',
                borderColor: selected ? 'primary.main' : 'grey.300',
                display: 'grid',
                placeItems: 'center',
                transition: 'border-color 140ms',
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  transform: selected ? 'scale(1)' : 'scale(0)',
                  transition: 'transform 160ms cubic-bezier(0.2,0,0,1)',
                }}
              />
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );
}
